import React, { useState } from 'react';
import { PACKAGES, PACKAGE_TYPES, PACKAGE_FEATURES } from '../../data/packageModels';
import { getPackageFeatures, isFeatureEnabled, getFeatureLimit, isUnlimited } from '../../data/packageModels';
import { useTranslation } from 'react-i18next';

const PackageSelectionModal = ({ isOpen, onClose, currentPackage = PACKAGE_TYPES.FREE, onSelectPackage }) => {
  const { t } = useTranslation();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  if (!isOpen) return null;

  const handleSelectPackage = (packageType) => {
    if (packageType === currentPackage) {
      alert(t('package.usingThisPackage'));
      return;
    }
    setSelectedPackage(packageType);
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    if (selectedPackage && onSelectPackage) {
      onSelectPackage(selectedPackage);
      setShowConfirm(false);
      setSelectedPackage(null);
    }
  };

  const getFeatureIcon = (feature) => {
    const icons = {
      [PACKAGE_FEATURES.MAX_COVER_IMAGES]: '🖼️',
      [PACKAGE_FEATURES.MAX_PRODUCT_IMAGES]: '📸',
      [PACKAGE_FEATURES.MAX_ACTIVE_PRODUCTS]: '📦',
      [PACKAGE_FEATURES.MAX_BANK_ACCOUNTS]: '🏦',
      [PACKAGE_FEATURES.QR_CODE_ENABLED]: '📱',
      [PACKAGE_FEATURES.QR_CODE_GENERATE]: '⚡',
      [PACKAGE_FEATURES.MAX_QR_CODE_GENERATE]: '⚡',
      [PACKAGE_FEATURES.CASH_ON_DELIVERY]: '📦',
      [PACKAGE_FEATURES.PICKUP_ENABLED]: '📍',
      [PACKAGE_FEATURES.PRIORITY_SUPPORT]: '⭐',
      [PACKAGE_FEATURES.ANALYTICS]: '📊',
      [PACKAGE_FEATURES.CUSTOM_DOMAIN]: '🌐'
    };
    return icons[feature] || '✓';
  };

  const getFeatureLabel = (feature) => {
    const labels = {
      [PACKAGE_FEATURES.MAX_COVER_IMAGES]: 'รูปปกร้าน',
      [PACKAGE_FEATURES.MAX_PRODUCT_IMAGES]: 'รูปสินค้า/ชิ้น',
      [PACKAGE_FEATURES.MAX_ACTIVE_PRODUCTS]: 'สินค้าพร้อมกัน',
      [PACKAGE_FEATURES.MAX_BANK_ACCOUNTS]: 'บัญชีธนาคาร',
      [PACKAGE_FEATURES.QR_CODE_ENABLED]: 'QR Code PromptPay (อัพโหลด)',
      [PACKAGE_FEATURES.QR_CODE_GENERATE]: 'QR Code Generate',
      [PACKAGE_FEATURES.MAX_QR_CODE_GENERATE]: 'QR Code Generate (จำนวน)',
      [PACKAGE_FEATURES.CASH_ON_DELIVERY]: 'เก็บปลายทาง (COD)',
      [PACKAGE_FEATURES.PICKUP_ENABLED]: 'จ่ายสด นัดรับ',
      [PACKAGE_FEATURES.PRIORITY_SUPPORT]: 'สนับสนุนแบบ Priority',
      [PACKAGE_FEATURES.ANALYTICS]: 'ระบบวิเคราะห์ข้อมูล',
      [PACKAGE_FEATURES.CUSTOM_DOMAIN]: 'โดเมนเฉพาะ'
    };
    return labels[feature] || feature;
  };

  const formatFeatureValue = (packageType, feature) => {
    const features = getPackageFeatures(packageType);
    const value = features[feature];
    
    if (feature === PACKAGE_FEATURES.QR_CODE_ENABLED || 
        feature === PACKAGE_FEATURES.QR_CODE_GENERATE ||
        feature === PACKAGE_FEATURES.CASH_ON_DELIVERY ||
        feature === PACKAGE_FEATURES.PICKUP_ENABLED ||
        feature === PACKAGE_FEATURES.PRIORITY_SUPPORT ||
        feature === PACKAGE_FEATURES.ANALYTICS ||
        feature === PACKAGE_FEATURES.CUSTOM_DOMAIN) {
      return value ? '✅' : '❌';
    }
    
    // สำหรับ MAX_QR_CODE_GENERATE
    if (feature === PACKAGE_FEATURES.MAX_QR_CODE_GENERATE) {
      if (value === 0) {
        return '❌';
      }
      if (isUnlimited(value)) {
        return '♾️ ไม่จำกัด';
      }
      return `${value} QR Codes`;
    }
    
    if (isUnlimited(value)) {
      return '♾️ ไม่จำกัด';
    }
    
    return value || '0';
  };

  const packageList = [
    PACKAGE_TYPES.FREE,
    PACKAGE_TYPES.BASIC,
    PACKAGE_TYPES.PREMIUM,
    PACKAGE_TYPES.ENTERPRISE
  ];

  return (
    <>
      {/* Modal Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div 
          className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-t-lg">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">{t('package.selectPackageForStore')}</h2>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <p className="text-green-100 mt-2">{t('package.selectPackageSuitable')}</p>
          </div>

          {/* Package Cards */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {packageList.map((packageType) => {
                const pkg = PACKAGES[packageType];
                const isCurrent = packageType === currentPackage;
                const isSelected = selectedPackage === packageType;
                
                return (
                  <div
                    key={packageType}
                    className={`relative border-2 rounded-lg p-6 transition-all cursor-pointer ${
                      isCurrent
                        ? 'border-green-500 bg-green-50'
                        : isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleSelectPackage(packageType)}
                  >
                    {isCurrent && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                        {t('package.current')}
                      </div>
                    )}
                    
                    {/* Package Header */}
                    <div className="text-center mb-4">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{pkg.name}</h3>
                      <div className="text-3xl font-bold text-green-600 mb-1">
                        {pkg.price === 0 ? t('package.free') : `฿${pkg.price.toLocaleString()}`}
                      </div>
                      {pkg.price > 0 && (
                        <div className="text-sm text-gray-500">/ {t('package.perMonth')}</div>
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-sm text-gray-600 text-center mb-4">{pkg.description}</p>

                    {/* Key Features */}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span>🖼️ รูปปกร้าน</span>
                        <span className="font-semibold">
                          {formatFeatureValue(packageType, PACKAGE_FEATURES.MAX_COVER_IMAGES)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>📸 รูปสินค้า</span>
                        <span className="font-semibold">
                          {formatFeatureValue(packageType, PACKAGE_FEATURES.MAX_PRODUCT_IMAGES)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>📦 สินค้าพร้อมกัน</span>
                        <span className="font-semibold">
                          {formatFeatureValue(packageType, PACKAGE_FEATURES.MAX_ACTIVE_PRODUCTS)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>🏦 บัญชีธนาคาร</span>
                        <span className="font-semibold">
                          {formatFeatureValue(packageType, PACKAGE_FEATURES.MAX_BANK_ACCOUNTS)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>📱 QR Code</span>
                        <span className="font-semibold">
                          {formatFeatureValue(packageType, PACKAGE_FEATURES.QR_CODE_ENABLED)}
                        </span>
                      </div>
                      {isFeatureEnabled(packageType, PACKAGE_FEATURES.QR_CODE_GENERATE) && (
                        <div className="flex items-center justify-between">
                          <span>⚡ Generate QR</span>
                          <span className="font-semibold">
                            {formatFeatureValue(packageType, PACKAGE_FEATURES.MAX_QR_CODE_GENERATE)}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span>📦 COD</span>
                        <span className="font-semibold">
                          {formatFeatureValue(packageType, PACKAGE_FEATURES.CASH_ON_DELIVERY)}
                        </span>
                      </div>
                    </div>

                    {/* Select Button */}
                    {!isCurrent && (
                      <button
                        className={`w-full mt-4 py-2 px-4 rounded-lg font-semibold transition-colors ${
                          isSelected
                            ? 'bg-blue-500 text-white hover:bg-blue-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectPackage(packageType);
                        }}
                      >
                        {isSelected ? `✓ ${t('package.selected')}` : t('package.selectThisPackage')}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Comparison Table */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('package.compareFeatures')}</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left">{t('package.feature')}</th>
                      {packageList.map((packageType) => (
                        <th key={packageType} className="border border-gray-300 px-4 py-2 text-center">
                          {PACKAGES[packageType].name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(PACKAGE_FEATURES).map((feature) => (
                      <tr key={feature}>
                        <td className="border border-gray-300 px-4 py-2">
                          {getFeatureIcon(feature)} {getFeatureLabel(feature)}
                        </td>
                        {packageList.map((packageType) => (
                          <td key={packageType} className="border border-gray-300 px-4 py-2 text-center">
                            {formatFeatureValue(packageType, feature)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 border-t p-4 flex justify-end gap-4">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-700 font-semibold"
            >
              {t('common.cancel')}
            </button>
            {selectedPackage && selectedPackage !== currentPackage && (
              <button
                onClick={handleConfirm}
                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold"
              >
                {t('package.subscribePackage')} {PACKAGES[selectedPackage].name}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && selectedPackage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">{t('package.confirmSubscription')}</h3>
            <div className="mb-4">
              <p className="text-gray-700 mb-2">
                {t('package.confirmSubscriptionMessage', { packageName: PACKAGES[selectedPackage].name })}
              </p>
              {PACKAGES[selectedPackage].price > 0 && (
                <p className="text-lg font-bold text-green-600">
                  {t('package.price')}: ฿{PACKAGES[selectedPackage].price.toLocaleString()}/{t('package.perMonth')}
                </p>
              )}
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShowConfirm(false);
                  setSelectedPackage(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-700 font-semibold"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold"
              >
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PackageSelectionModal;



